import React, { useEffect, useRef, useState } from 'react';
import { GraduationCap, Zap, Sparkles, Play, Pause, Users, Volume2, X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

// Snaptrude final avatar cast, shared with Uzma 2026-10-29.
// Faces + voice demos only. Production IDs (HeyGen / ElevenLabs) stay internal:
// clients-current/snaptrude/avatars/registry.json. No em dashes in copy.

const A = '/assets/snaptrude/cast';

const CAST = [
    {
        key: 'aes', page: 'Page 4', title: 'The Aspiring Architect', sub: 'Final-year student building her portfolio', Icon: Sparkles,
        ring: 'border-fuchsia-400/30', chip: 'bg-fuchsia-500/15 text-fuchsia-300', btn: 'bg-fuchsia-500 hover:bg-fuchsia-400',
        looks: [`${A}/architect-main.jpg`, `${A}/architect-2.jpg`], audio: `${A}/architect.mp3`,
        voice: 'Two voice options for her. Same line in each, so you can pick the one that feels like her.',
        options: [
            { label: 'Option 1 · Upbeat', note: 'Girl-next-door, never polished', audio: `${A}/architect.mp3` },
            { label: 'Option 2 · Warm', note: 'Warm and conversational', audio: `${A}/architect-eliza.mp3` },
        ],
        about: 'Portfolio fixes, studio survival, and a thesis diary her followers come back for.',
        line: '"My professor said my portfolio had no story. Honestly? She was right."',
    },
    {
        key: 'loud', page: 'Page 3', title: 'The Tech Graduate', sub: 'Knows the new tools before anyone in the office', Icon: Zap,
        ring: 'border-orange-400/30', chip: 'bg-orange-500/15 text-orange-300', btn: 'bg-orange-500 hover:bg-orange-400',
        looks: [`${A}/techgrad-1.jpg`, `${A}/techgrad-2.jpg`], audio: `${A}/techgrad.mp3`,
        voice: 'Friendly, fast and conversational, like a mate showing you a shortcut.',
        about: 'One AI trick per task, one-minute fixes, honest tool tests and speed runs.',
        line: '"My boss spent two days on this. I did it before lunch."',
    },
    {
        key: 'auth', page: 'Page 2', title: 'The Professor', sub: 'Teaches architecture, 25+ years in studio', Icon: GraduationCap,
        ring: 'border-sky-400/30', chip: 'bg-sky-500/15 text-sky-300', btn: 'bg-sky-500 hover:bg-sky-400',
        looks: [`${A}/professor-1.jpg`, `${A}/professor-2.jpg`], audio: `${A}/professor.mp3`,
        voice: 'Calm, measured narration. Speaks like a favourite teacher.',
        about: 'Design fundamentals, drawn vs built, and old rules with new tools.',
        line: '"The first hour of a project decides more than the next six months."',
    },
];


function VoiceButton({ btn, playing, onToggle }) {
    return (
        <button onClick={onToggle} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white transition ${btn}`}>
            {playing ? <Pause size={16} /> : <Play size={16} />} {playing ? 'Pause' : 'Hear the voice'}
        </button>
    );
}

export default function SnaptrudeCast() {
    useEffect(() => { document.title = 'SocialSEO | Snaptrude Creator Cast'; }, []);
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(null);
    const [lb, setLb] = useState(null); // { title, looks, i }

    useEffect(() => {
        if (!lb) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setLb(null);
            if (e.key === 'ArrowRight') setLb((v) => v && { ...v, i: (v.i + 1) % v.looks.length });
            if (e.key === 'ArrowLeft') setLb((v) => v && { ...v, i: (v.i - 1 + v.looks.length) % v.looks.length });
        };
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    }, [lb]);

    const step = (d) => (e) => { e.stopPropagation(); setLb((v) => v && { ...v, i: (v.i + d + v.looks.length) % v.looks.length }); };

    const toggle = (id, src) => {
        const el = audioRef.current;
        if (!el) return;
        if (playing === id) { el.pause(); setPlaying(null); return; }
        el.src = src;
        el.currentTime = 0;
        el.play().then(() => setPlaying(id)).catch(() => setPlaying(null));
    };

    return (
        <div className="min-h-screen text-white">
            <audio ref={audioRef} onEnded={() => setPlaying(null)} preload="none" />
            <header className="fixed top-4 inset-x-0 z-50 flex justify-center px-4">
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur px-4 py-2 text-xs">
                    <span className="font-black">Social<span className="text-indigo-400">[SEO]</span></span>
                    <span className="text-slate-500">×</span>
                    <span className="text-slate-300">Snaptrude</span>
                    <span className="ml-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-300 font-bold">Cast locked</span>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-28 pb-24 flex flex-col gap-20">
                <section className="flex flex-col gap-5">
                    <div className="inline-flex self-start items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-300">
                        <Users size={14} /> The creator cast
                    </div>
                    <h1 className="text-4xl sm:text-6xl font-black leading-[1.05] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                        Meet the three faces of your creator pages.
                    </h1>
                    <p className="text-lg text-slate-400 max-w-3xl">
                        Each creator now has a final face and a final voice, shown here in two looks. Tap play to hear how they sound. Every video they post carries the platform's AI label.
                    </p>
                </section>

                <section className="grid lg:grid-cols-3 gap-5">
                    {CAST.map((c) => (
                        <article key={c.key} className={`rounded-3xl border ${c.ring} bg-white/5 backdrop-blur overflow-hidden flex flex-col`}>
                            <div className={`relative grid gap-1 bg-black/30 ${c.looks.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                {c.looks.map((src, i) => (
                                    <button key={src} type="button" onClick={() => setLb({ title: c.title, looks: c.looks, i })} className="group relative block cursor-zoom-in" aria-label={`Open ${c.title}, look ${i + 1}, full screen`}>
                                        <img src={src} alt={`${c.title}, look ${i + 1}`} className={`w-full object-cover ${c.looks.length > 1 ? 'aspect-[9/16]' : 'aspect-[9/8] object-[50%_40%]'}`} loading="lazy" />
                                        <span className="absolute bottom-2 right-2 rounded-full bg-black/60 p-1.5 text-white opacity-80 group-hover:opacity-100"><Maximize2 size={14} /></span>
                                    </button>
                                ))}
                                <span className={`pointer-events-none absolute top-4 left-4 rounded-full bg-black/60 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur`}>{c.page}</span>
                            </div>
                            <div className="p-6 flex flex-col gap-4 flex-1">
                                <div>
                                    <h3 className="text-2xl font-black">{c.title}</h3>
                                    <div className="text-sm text-slate-400">{c.sub}</div>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-black/20 p-4 flex flex-col gap-3">
                                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500"><Volume2 size={14} /> Voice</div>
                                    <p className="text-sm text-slate-300 leading-relaxed">{c.voice}</p>
                                    <p className="text-sm italic text-slate-400">{c.line}</p>
                                    {c.options ? (
                                        <ul className="flex flex-col gap-2">
                                            {c.options.map((o, i) => {
                                                const id = `${c.key}-${i}`;
                                                const on = playing === id;
                                                return (
                                                    <li key={id}>
                                                        <button type="button" onClick={() => toggle(id, o.audio)} className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2 text-left transition ${on ? 'border-fuchsia-400 bg-fuchsia-500/20' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
                                                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white ${c.btn}`}>{on ? <Pause size={14} /> : <Play size={14} />}</span>
                                                            <span className="min-w-0">
                                                                <span className="block text-sm font-bold text-white">{o.label}</span>
                                                                <span className="block text-xs text-slate-400">{o.note}</span>
                                                            </span>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    ) : (
                                        <div><VoiceButton btn={c.btn} playing={playing === c.key} onToggle={() => toggle(c.key, c.audio)} /></div>
                                    )}
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Posts about</div>
                                    <div className="text-sm text-slate-300 leading-relaxed mt-0.5">{c.about}</div>
                                </div>
                            </div>
                        </article>
                    ))}
                </section>

            </main>

            {lb && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setLb(null)} role="dialog" aria-modal="true" aria-label={`${lb.title}, look ${lb.i + 1} of ${lb.looks.length}`}>
                    <img src={lb.looks[lb.i]} alt={`${lb.title}, look ${lb.i + 1}`} className="max-h-[88vh] max-w-full rounded-2xl object-contain" onClick={(e) => e.stopPropagation()} />
                    <button type="button" onClick={() => setLb(null)} className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Close"><X size={22} /></button>
                    {lb.looks.length > 1 && (
                        <>
                            <button type="button" onClick={step(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Previous look"><ChevronLeft size={26} /></button>
                            <button type="button" onClick={step(1)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Next look"><ChevronRight size={26} /></button>
                        </>
                    )}
                    <div className="absolute bottom-4 inset-x-0 text-center text-sm text-slate-300">{lb.title} · look {lb.i + 1} of {lb.looks.length}</div>
                </div>
            )}
        </div>
    );
}
